import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Clock, 
  ArrowLeft, 
  RotateCcw, 
  Globe, 
  Calendar, 
  Sun, 
  Moon,
  Sparkles 
} from 'lucide-react';

export const TimeZoneConverter = () => {
  const { withAd, toast } = useAdRedirect();
  const [sourceTime, setSourceTime] = useState('12:00');
  const [sourceTz, setSourceTz] = useState('UTC');

  // Timezones Pool relative offset to UTC in hours
  const tzPool = {
    'UTC': { name: 'Universal Time Coordinated', offset: 0, region: 'Global' },
    'EST': { name: 'Eastern Standard Time (New York)', offset: -5, region: 'US' },
    'PST': { name: 'Pacific Standard Time (Los Angeles)', offset: -8, region: 'US' },
    'GMT': { name: 'Greenwich Mean Time (London)', offset: 0, region: 'Europe' },
    'CET': { name: 'Central European Time (Paris/Berlin)', offset: 1, region: 'Europe' },
    'GST': { name: 'Gulf Standard Time (Dubai)', offset: 4, region: 'Middle East' },
    'IST': { name: 'Indian Standard Time (New Delhi)', offset: 5.5, region: 'Asia' },
    'SGT': { name: 'Singapore Time (Singapore)', offset: 8, region: 'Asia' },
    'JST': { name: 'Japan Standard Time (Tokyo)', offset: 9, region: 'Asia' },
    'AEST': { name: 'Australian Eastern Standard (Sydney)', offset: 10, region: 'Australia' }
  };

  const convertTimezones = () => {
    let [hrs, mins] = sourceTime.split(':').map(Number);
    if (isNaN(hrs) || isNaN(mins)) return [];

    let srcOffset = tzPool[sourceTz]?.offset || 0;

    // Convert source to UTC absolute decimal hours
    let utcDecimalHrs = hrs + (mins / 60) - srcOffset;

    let results = Object.keys(tzPool).map(code => {
      let targetOffset = tzPool[code].offset;
      let targetDecimalHrs = utcDecimalHrs + targetOffset;

      // normalize 24 hrs
      let dayShift = 0;
      if (targetDecimalHrs < 0) {
        targetDecimalHrs += 24;
        dayShift = -1;
      } else if (targetDecimalHrs >= 24) {
        targetDecimalHrs -= 24;
        dayShift = 1;
      }

      let finalHrs = Math.floor(targetDecimalHrs);
      let finalMins = Math.round((targetDecimalHrs - finalHrs) * 60);

      if (finalMins === 60) {
        finalHrs = (finalHrs + 1) % 24;
        finalMins = 0;
      }

      let ampm = finalHrs >= 12 ? 'PM' : 'AM';
      let dispHrs = finalHrs % 12 || 12;

      let formattedTime = `${dispHrs.toString().padStart(2, '0')}:${finalMins.toString().padStart(2, '0')} ${ampm}`;

      // Daylight / Business hours indicator (9 AM to 5 PM)
      let isBusinessHours = finalHrs >= 9 && finalHrs < 17;
      let diffHrs = targetOffset - srcOffset;

      return {
        code,
        name: tzPool[code].name,
        time: formattedTime,
        diff: diffHrs >= 0 ? `+${diffHrs} hrs` : `${diffHrs} hrs`,
        dayShift: dayShift === 0 ? 'Same Day' : dayShift > 0 ? 'Next Day' : 'Previous Day',
        isBusinessHours,
        region: tzPool[code].region
      };
    });

    return results;
  };

  const tzResults = convertTimezones();

  const handleReset = () => {
    setSourceTime('12:00');
    setSourceTz('UTC');
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Time Zone Converter & Meeting Planner" 
        description="Convert local time across New York, London, Tokyo, Sydney, Paris, and global timezones. Plan international meetings seamlessly."
        keywords="time zone converter, timezone calculator, est to pst, utc converter, international meeting planner, world clock"
      />
      
      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Dashboard
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--primary)', fontWeight: 600 }}>Time Zone Converter</span>
      </div>

      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 8px 0' }}>
          Time Zone Converter & Meeting Planner
        </h1>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', margin: 0 }}>
          Synchronize remote team schedules across major global financial hubs with daylight business hour indicators.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="tool-grid-split">
        
        {/* Left Side: Inputs */}
        <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)', alignSelf: 'start' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Clock size={18} style={{ color: 'var(--primary)' }} /> Source Time Selection
            </h3>
            <button onClick={handleReset} style={{ background: 'rgba(15, 23, 42, 0.04)', border: 'none', padding: '6px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <RotateCcw size={14} /> Clear
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Source Time</label>
              <input 
                type="time" 
                value={sourceTime} 
                onChange={(e) => setSourceTime(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '1.1rem', fontWeight: 700, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)' }}>Source Timezone</label>
              <select 
                value={sourceTz} 
                onChange={(e) => setSourceTz(e.target.value)}
                style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.95rem', fontWeight: 600, outline: 'none', background: 'var(--bg-secondary)', boxSizing: 'border-box' }}
              >
                {Object.keys(tzPool).map(code => (
                  <option key={code} value={code}>{code} - {tzPool[code].name}</option>
                ))}
              </select>
            </div>

            <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '4px' }}>Meeting Hours Parity</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Green highlighted slots indicate standard business hours (9 AM - 5 PM) for optimal international scheduling.</div>
            </div>
          </div>
        </div>

        {/* Right Side: Dashboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          <div style={{ background: '#ffffff', border: '1px solid var(--border-color)', borderRadius: '20px', padding: '24px', boxShadow: 'var(--shadow-sm)' }}>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Globe size={18} style={{ color: '#10b981' }} /> Global Timezones Board
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {tzResults.map((item) => (
                <div key={item.code} style={{ background: 'var(--bg-secondary)', padding: '14px 18px', borderRadius: '14px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span style={{ fontSize: '0.95rem', fontWeight: 800, color: 'var(--text-primary)' }}>{item.code}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', background: '#ffffff', padding: '2px 8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>{item.region}</span>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{item.name}</div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '1.15rem', fontWeight: 800, color: item.isBusinessHours ? '#10b981' : 'var(--text-primary)' }}>
                        {item.time}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                        {item.diff} ({item.dayShift})
                      </div>
                    </div>

                    <div style={{ background: item.isBusinessHours ? 'rgba(16, 185, 129, 0.1)' : 'rgba(15, 23, 42, 0.04)', color: item.isBusinessHours ? '#10b981' : 'var(--text-muted)', padding: '8px', borderRadius: '10px' }}>
                      {item.isBusinessHours ? <Sun size={18} /> : <Moon size={18} />}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '28px', textAlign: 'center' }}>
              <AdToast message={toast} />
              <button 
                onClick={withAd('timezone-btn', () => alert("Timezone schedule verified client-side."))}
                style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', padding: '12px 28px', borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer', transition: 'var(--transition-smooth)' }}
              >
                Log Meeting Planner
              </button>
            </div>
          </div>

        </div>

      </div>

      <style>{`
        @media (min-width: 992px) {
          .tool-grid-split { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>

      <AdUnit />
      <ToolPageExtras toolId="time-zone-converter" />
    </div>
  );
};

export default TimeZoneConverter;
